/**
 * DocumentsTab — Phase 36 Batch AA.5 Feature 1 Batch B
 *
 * The user-facing payoff for the document-analysis backend. Lives on
 * the deal-detail page between Risk and Negotiation. Handles:
 *   • drag-and-drop OR tap-to-upload with required category selector
 *   • inline client-side validation (size, mime, count) before the
 *     multipart POST even fires
 *   • a card per uploaded document — analyzing spinner OR full AI
 *     analysis once status=complete
 *   • 5s polling (5-minute ceiling) while any doc is mid-pipeline
 *   • view-original / re-analyze / delete actions
 *   • PostHog tracking on upload + analyze-complete + delete
 *
 * NEVER imports raw axios — uses the shared `api` instance so the
 * centralized 401 interceptor handles the well-known auth-race on
 * first-load (see /app/frontend/src/lib/api.js).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";import {
  Upload,
  FileText,
  Loader2,
  Check,
  X,
  AlertCircle,
  Trash2,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MAX_FILE_BYTES = 10 * 1024 * 1024; // keep in sync w/ backend
const MAX_DOCS_PER_DEAL = 10;
const ALLOWED_MIMES = ["application/pdf", "image/jpeg", "image/png"];
const ALLOWED_EXTS = [".pdf", ".jpg", ".jpeg", ".png"];

const CATEGORIES = [
  { value: "inspection", label: "Inspection Report" },
  { value: "hoa_financials", label: "HOA Financials" },
  { value: "hoa_minutes", label: "HOA Board Minutes" },
  { value: "insurance", label: "Insurance Quote / Policy" },
  { value: "title_survey", label: "Title / Survey" },
  { value: "appraisal_bpo", label: "Appraisal / BPO" },
  { value: "comparables_cma", label: "Comparables / CMA" },
  { value: "lease_rent_roll", label: "Lease / Rent Roll" },
  { value: "other", label: "Other (custom)" },
];
const CATEGORY_LABEL_BY_VALUE = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.label])
);

// Statuses where the pipeline is still in-flight on the backend.
const ANALYZING_STATUSES = new Set(["pending", "extracting", "classifying", "analyzing"]);

const POLL_INTERVAL_MS = 5000;
const POLL_CEILING_MS = 5 * 60 * 1000;

const VERDICT_STYLES = {
  strong: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  acceptable: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
  concerns: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
  reject: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
};
const VERDICT_LABELS = {
  strong: "Strong",
  acceptable: "Acceptable",
  concerns: "Concerns",
  reject: "Reject",
};

const CATEGORY_BADGE_STYLES = {
  inspection: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  hoa_financials: "bg-amber-50 text-amber-800 ring-amber-200",
  hoa_minutes: "bg-amber-50 text-amber-800 ring-amber-200",
  insurance: "bg-sky-50 text-sky-700 ring-sky-200",
  title_survey: "bg-violet-50 text-violet-700 ring-violet-200",
  appraisal_bpo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  comparables_cma: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  lease_rent_roll: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  other: "bg-slate-50 text-slate-700 ring-slate-200",
};

function ratingColor(rating) {
  if (rating == null) return "bg-slate-100 text-slate-600 ring-slate-200";
  if (rating >= 8) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (rating >= 5) return "bg-amber-50 text-amber-800 ring-amber-200";
  return "bg-rose-50 text-rose-700 ring-rose-200";
}

function track(event, props) {
  try {
    window.posthog?.capture?.(event, props);
  } catch {
    /* DNT / blocked — ignore */
  }
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}


export default function DocumentsTab({ deal }) {
  const dealId = deal?.id;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // upload form state — Phase 36 Batch AC: defaults to "auto" so the
  // LLM picks the category from the filename + extracted text. User can
  // still pick a specific category to override.
  const [category, setCategory] = useState("auto");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // delete-confirm modal
  const [docToDelete, setDocToDelete] = useState(null);

  // ──── load + refresh ────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/deals/${dealId}/documents`);
      setDocs(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError("Could not load documents. Try refreshing.");
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ──── polling for in-flight analyses ────────────────────────────────
  // We poll a deal's documents endpoint every 5s while any of them is
  // in pending/extracting/analyzing. Polling auto-stops once the doc
  // resolves to complete/failed, or after a 5-minute ceiling — after
  // that, the UI shows a "Refresh manually" banner instead of hammering
  // the backend.
  //
  // Re-running the effect is gated on `analyzingIds` rather than the
  // entire `docs` array, so each ticker setDocs call doesn't kick off
  // a fresh interval (which would create a polling storm).
  const analyzingIds = useMemo(
    () =>
      docs
        .filter((d) => ANALYZING_STATUSES.has(d.analysis_status))
        .map((d) => d.id)
        .sort()
        .join(","),
    [docs]
  );

  const [pollStuck, setPollStuck] = useState(false);

  // Async polling — the canonical React pattern for waiting on remote
  // state to settle. `docs` and `pollStuck` are driven by an external,
  // asynchronous source (the documents endpoint), which is exactly
  // when a setState-in-effect is the correct shape.
  useEffect(() => {
    if (!analyzingIds) {
      setPollStuck(false);
      return undefined;
    }
    const ids = analyzingIds.split(",");
    let alive = true;
    const startedAt = Date.now();

    const tick = async () => {
      if (Date.now() - startedAt > POLL_CEILING_MS) {
        setPollStuck(true);
        return;
      }
      await Promise.all(
        ids.map(async (id) => {
          try {
            const { data } = await api.get(`/deals/${dealId}/documents/${id}`);
            if (!alive) return;
            // setState updater must stay pure — so the side-effecty
            // posthog `document_analyzed` track call lives OUTSIDE the
            // updater, gated on the same just-resolved condition.
            let wasJustCompleted = false;
            setDocs((curr) => {
              const prev = curr.find((x) => x.id === id);
              if (
                data.analysis_status === "complete" &&
                prev?.analysis_status !== "complete"
              ) {
                wasJustCompleted = true;
              }
              return curr.map((x) => (x.id === id ? data : x));
            });
            if (wasJustCompleted) {
              track("document_analyzed", {
                deal_id: dealId,
                doc_id: data.id,
                category: data.category,
                rating: data.ai_analysis?.rating,
                verdict: data.ai_analysis?.verdict,
              });
            }
          } catch {
            /* transient — keep polling */
          }
        })
      );
    };

    const handle = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      alive = false;
      clearInterval(handle);
    };
  }, [analyzingIds, dealId]);

  // ──── upload ────────────────────────────────────────────────────────
  const validateAndUpload = useCallback(
    async (file) => {
      setUploadError(null);
      // Phase 36 Batch AC — category is no longer strictly required;
      // "auto" (the default) lets the backend classify post-extraction.
      if (!category) {
        setUploadError("Pick a category first.");
        return;
      }
      if (!file) return;
      const nameLower = (file.name || "").toLowerCase();
      const hasOkExt = ALLOWED_EXTS.some((e) => nameLower.endsWith(e));
      if (!ALLOWED_MIMES.includes(file.type) && !hasOkExt) {
        setUploadError("Only PDF, JPG, and PNG files are supported.");
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        setUploadError(
          `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`
        );
        return;
      }
      if (docs.length >= MAX_DOCS_PER_DEAL) {
        setUploadError(`Max ${MAX_DOCS_PER_DEAL} documents per deal reached.`);
        return;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        // Backend treats "auto" as the sentinel that defers
        // classification to the LLM during the background pipeline.
        fd.append("category", category);
        if (category === "other" && categoryLabel.trim()) {
          fd.append("category_label", categoryLabel.trim());
        }
        const { data } = await api.post(
          `/deals/${dealId}/documents`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        track("document_uploaded", {
          deal_id: dealId,
          category,
          auto_classify: category === "auto",
          file_size: file.size,
          mime_type: file.type,
        });
        setDocs((curr) => [data, ...curr]);
        toast.success(
          category === "auto"
            ? "Uploaded — AI is detecting the category…"
            : "Uploaded — running AI analysis…"
        );
        // Reset the form (but keep the category — most users upload a batch
        // of the same type in a row).
        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch (e) {
        const msg =
          e?.response?.data?.detail ||
          e?.message ||
          "Upload failed. Try again.";
        setUploadError(typeof msg === "string" ? msg : "Upload failed.");
        toast.error("Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [category, categoryLabel, docs.length, dealId]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) validateAndUpload(file);
    },
    [validateAndUpload]
  );

  // ──── delete / re-analyze ───────────────────────────────────────────
  const confirmDelete = async () => {
    if (!docToDelete) return;
    const { id: doc_id, category: cat } = docToDelete;
    setDocToDelete(null);
    try {
      await api.delete(`/deals/${dealId}/documents/${doc_id}`);
      track("document_deleted", { deal_id: dealId, doc_id, category: cat });
      setDocs((curr) => curr.filter((d) => d.id !== doc_id));
      toast.success("Document deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const reanalyze = async (doc) => {
    try {
      const { data } = await api.post(
        `/deals/${dealId}/documents/${doc.id}/reanalyze`
      );
      // Force the doc back into analyzing so the polling effect kicks in.
      setDocs((curr) => curr.map((x) => (x.id === data.id ? data : x)));
      setPollStuck(false); // reset the 5-min ceiling — we're trying again
      toast.success("Re-running AI analysis…");
    } catch {
      toast.error("Could not re-run analysis");
    }
  };

  // Phase 36 Batch AC — user-correction of an auto-classified
  // category. Sends the PATCH and optimistically swaps the doc in
  // local state so the badge updates immediately.
  const changeCategory = useCallback(
    async (doc, newCategory) => {
      if (!newCategory || newCategory === doc.category) return;
      const previousCategory = doc.category;
      try {
        const { data } = await api.patch(
          `/deals/${dealId}/documents/${doc.id}/category`,
          { category: newCategory }
        );
        setDocs((curr) => curr.map((x) => (x.id === data.id ? data : x)));
        track("document_category_corrected", {
          deal_id: dealId,
          doc_id: doc.id,
          from_category: previousCategory,
          to_category: newCategory,
          was_auto_classified: Boolean(doc.auto_classified),
        });
        toast.success("Category updated");
      } catch {
        toast.error("Could not update category");
      }
    },
    [dealId]
  );

  const openOriginal = (doc) => {
    // Opens via the shared api baseURL so cookies flow on same-origin.
    // We can't add an Authorization header to <a> clicks, so we rely
    // on the httpOnly cookie session being attached automatically.
    const base = api.defaults.baseURL || "";
    const url = `${base}/deals/${dealId}/documents/${doc.id}/file`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ──── render ────────────────────────────────────────────────────────
  const atLimit = docs.length >= MAX_DOCS_PER_DEAL;
  const anyAnalyzing = docs.some((d) => ANALYZING_STATUSES.has(d.analysis_status));

  return (
    <div className="space-y-6" data-testid="documents-tab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            Documents
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Upload inspections, HOA financials, board minutes, and any other
            deal docs. AI grades each one with a rating, verdict, top findings,
            and red flags.
          </p>
        </div>
        <div className="text-xs text-slate-500" data-testid="docs-counter">
          {docs.length}/{MAX_DOCS_PER_DEAL} documents
        </div>
      </div>

      {/* Upload zone */}
      <section
        data-testid="docs-upload-zone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed p-6 transition-colors ${
          dragOver
            ? "border-indigo-400 bg-indigo-50/60"
            : "border-slate-300 bg-white"
        } ${atLimit ? "opacity-60" : ""}`}
      >
        <div className="grid gap-4 md:grid-cols-[1fr_auto]">
          <div>
            <div className="flex items-center gap-2 text-slate-700">
              <Upload className="h-5 w-5" />
              <span className="font-semibold">
                {atLimit
                  ? `${MAX_DOCS_PER_DEAL}/${MAX_DOCS_PER_DEAL} documents uploaded — delete one to add more`
                  : "Drag a PDF / JPG / PNG here, or click to choose"}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Max 10 MB per file. Original is stored encrypted in MongoDB
              GridFS; the AI sees only the extracted text.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Category
                </Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="docs-category-select">
                    <SelectValue placeholder="Pick a category…" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Phase 36 Batch AC — auto-detect is the first &
                        default option. LLM classifies post-extraction. */}
                    <SelectItem value="auto" data-testid="docs-category-auto">
                      Auto-detect (recommended)
                    </SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value} data-testid={`docs-category-${c.value}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {category === "other" ? (
                <div className="flex flex-col gap-1">
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Custom label
                  </Label>
                  <Input
                    data-testid="docs-category-label"
                    value={categoryLabel}
                    onChange={(e) => setCategoryLabel(e.target.value)}
                    placeholder="e.g. Engineering Report"
                  />
                </div>
              ) : null}
            </div>

            {uploadError ? (
              <div
                data-testid="docs-upload-error"
                className="mt-3 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700"
              >
                <AlertCircle className="h-4 w-4 flex-none" />
                <span>{uploadError}</span>
              </div>
            ) : null}
          </div>

          <div className="flex items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              className="sr-only"
              data-testid="docs-file-input"
              onChange={(e) => validateAndUpload(e.target.files?.[0])}
              disabled={atLimit || uploading}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={atLimit || uploading || !category}
              data-testid="docs-upload-button"
              className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" /> Choose file
                </>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Document list */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Loading documents…
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
          {error}
        </div>
      ) : docs.length === 0 ? (
        <DocumentsEmptyState />
      ) : (
        <div className="space-y-4" data-testid="docs-list">
          {docs.map((doc) => (
            <DocumentCard
              key={doc.id}
              doc={doc}
              onDelete={() => setDocToDelete(doc)}
              onReanalyze={() => reanalyze(doc)}
              onChangeCategory={(nc) => changeCategory(doc, nc)}
              onView={() => openOriginal(doc)}
            />
          ))}
        </div>
      )}

      {anyAnalyzing && pollStuck && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Analysis is taking longer than expected.{" "}
          <button
            type="button"
            data-testid="docs-manual-refresh"
            onClick={refresh}
            className="font-semibold underline"
          >
            Refresh now
          </button>
        </div>
      )}

      <AlertDialog
        open={!!docToDelete}
        onOpenChange={(open) => !open && setDocToDelete(null)}
      >
        <AlertDialogContent data-testid="docs-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              The file and its AI analysis will be permanently removed. This
              can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="docs-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="docs-delete-confirm-btn"
              onClick={confirmDelete}
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function DocumentsEmptyState() {
  return (
    <div
      data-testid="docs-empty"
      className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center"
    >
      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
        <FileText className="h-6 w-6" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900">
        No documents yet
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
        Upload inspections, HOA financials, board minutes, certifications, or
        any deal docs to get AI-powered insights and a Document Health rating.
      </p>
    </div>
  );
}


function DocumentCard({ doc, onDelete, onReanalyze, onChangeCategory, onView }) {
  const status = doc.analysis_status;
  const analyzing = ANALYZING_STATUSES.has(status);
  const ai = doc.ai_analysis;
  const catLabel = doc.category === "other" && doc.category_label
    ? doc.category_label
    : (CATEGORY_LABEL_BY_VALUE[doc.category] || doc.category);
  const catCls = CATEGORY_BADGE_STYLES[doc.category] || CATEGORY_BADGE_STYLES.other;
  // Phase 36 Batch AC — auto-classified badge state + override picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const showAutoBadge =
    doc.auto_classified === true &&
    doc.classification_confidence != null &&
    status !== "classifying" &&
    !doc.user_corrected;
  const confPct = doc.classification_confidence != null
    ? Math.round(doc.classification_confidence * 100)
    : null;

  return (
    <article
      data-testid={`docs-card-${doc.id}`}
      data-status={status}
      data-auto-classified={doc.auto_classified ? "true" : "false"}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ${catCls}`}>
              {catLabel}
            </span>
            {status === "complete" && ai?.rating != null ? (
              <span
                data-testid={`docs-rating-${doc.id}`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ring-1 ${ratingColor(ai.rating)}`}
              >
                {ai.rating}/10
              </span>
            ) : null}
            {status === "complete" && ai?.verdict ? (
              <span
                data-testid={`docs-verdict-${doc.id}`}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${VERDICT_STYLES[ai.verdict] || VERDICT_STYLES.concerns}`}
              >
                {VERDICT_LABELS[ai.verdict] || ai.verdict}
              </span>
            ) : null}
            {status === "classifying" ? (
              <span
                data-testid={`docs-classifying-${doc.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-200"
              >
                <Sparkles className="h-3 w-3" /> Auto-classifying…
              </span>
            ) : null}
            {analyzing && status !== "classifying" ? (
              <span
                data-testid={`docs-analyzing-${doc.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-200"
              >
                <Loader2 className="h-3 w-3 animate-spin" /> Analyzing…
              </span>
            ) : null}
            {status === "failed" ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200">
                <X className="h-3 w-3" /> Failed
              </span>
            ) : null}
          </div>
          <h4 className="mt-2 truncate text-sm font-semibold text-slate-900" title={doc.original_filename}>
            {doc.original_filename}
          </h4>
          <p className="mt-0.5 text-xs text-slate-500">
            Uploaded {relativeTime(doc.uploaded_at)} ·{" "}
            {(doc.file_size_bytes / 1024).toFixed(0)} KB
            {ai?.confidence != null ? (
              <> · AI confidence {Math.round(ai.confidence * 100)}%</>
            ) : null}
          </p>
          {/* Phase 36 Batch AC — auto-detected sub-line + override
              picker. Only renders when the doc was auto-classified
              AND the user hasn't yet corrected it. */}
          {showAutoBadge ? (
            <div
              data-testid={`docs-auto-tag-${doc.id}`}
              className="mt-2 flex flex-wrap items-center gap-2 text-xs"
            >
              <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700 ring-1 ring-indigo-100">
                <Sparkles className="h-3 w-3" /> Auto-detected
                {confPct != null ? <span className="font-semibold">{" "}({confPct}% confidence)</span> : null}
              </span>
              {!pickerOpen ? (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  data-testid={`docs-change-category-${doc.id}`}
                  className="text-indigo-700 underline-offset-2 hover:underline"
                >
                  Change category
                </button>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Select
                    value={doc.category}
                    onValueChange={(nc) => {
                      onChangeCategory?.(nc);
                      setPickerOpen(false);
                    }}
                  >
                    <SelectTrigger
                      data-testid={`docs-category-picker-${doc.id}`}
                      className="h-7 w-44 text-xs"
                    >
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem
                          key={c.value}
                          value={c.value}
                          data-testid={`docs-category-picker-${doc.id}-${c.value}`}
                        >
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(false)}
                    aria-label="Cancel"
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          ) : null}
          {doc.user_corrected ? (
            <div
              data-testid={`docs-user-corrected-${doc.id}`}
              className="mt-2 inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
            >
              <Check className="h-3 w-3" /> Category set by you
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onView}
            data-testid={`docs-view-${doc.id}`}
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <ExternalLink className="mr-1 h-4 w-4" /> View original
          </Button>
          {status === "complete" || status === "failed" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onReanalyze}
              data-testid={`docs-reanalyze-${doc.id}`}
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="mr-1 h-4 w-4" /> Re-analyze
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDelete}
            data-testid={`docs-delete-${doc.id}`}
            className="border-rose-200 text-rose-700 hover:bg-rose-50"
          >
            <Trash2 className="mr-1 h-4 w-4" /> Delete
          </Button>
        </div>
      </header>

      {status === "failed" && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {doc.analysis_error || "AI analysis failed. Try re-analyzing."}
        </div>
      )}

      {status === "complete" && ai ? (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {ai.findings?.length > 0 && (
            <div>
              <h5 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                <ShieldCheck className="h-3.5 w-3.5" /> Top findings
              </h5>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                {ai.findings.map((f, i) => (
                  <li key={`f-${i}-${f.slice(0, 24)}`} className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-emerald-500" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {ai.red_flags?.length > 0 && (
            <div>
              <h5 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
                <ShieldAlert className="h-3.5 w-3.5" /> Red flags
              </h5>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                {ai.red_flags.map((f, i) => (
                  <li key={`r-${i}-${f.slice(0, 24)}`} className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-rose-500" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {ai.ai_summary && (
            <div className="lg:col-span-2">
              <h5 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                <Sparkles className="h-3.5 w-3.5" /> AI summary
              </h5>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {ai.ai_summary}
              </p>
            </div>
          )}
          {ai.category_specific &&
            Object.keys(ai.category_specific).length > 0 && (
            <div className="lg:col-span-2 rounded-xl bg-slate-50 p-4">
              <h5 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Category details
              </h5>
              <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                {Object.entries(ai.category_specific).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="font-medium text-slate-500 capitalize">
                      {k.replace(/_/g, " ")}:
                    </dt>
                    <dd className="text-slate-800">
                      {Array.isArray(v)
                        ? v.length > 0 ? v.join("; ") : "—"
                        : v == null
                          ? "—"
                          : typeof v === "boolean"
                            ? (v ? "Yes" : "No")
                            : String(v)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      ) : null}

      {analyzing && (
        <div className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600" />
            Running due-diligence analysis — usually 10–30 seconds. We&apos;ll
            refresh this card automatically when it&apos;s ready.
          </span>
        </div>
      )}
    </article>
  );
}
